import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import '../design/styles/nav.css'
import auth from '../hooks/auth'
import { ChevronLeft, ChevronRight } from "lucide-react";

const Nav = ( props ) => {

const scrollRef = useRef(null);
const titlesRef = useRef(null);
const [arrows, setArrows] = useState({ left: false, right: false });
const [touchNav, setTouchNav] = useState(
  () => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches
);
const [openMenuKey, setOpenMenuKey] = useState(null);

useEffect(() => {
  const mq = window.matchMedia('(hover: none)');
  const onChange = () => {
    setTouchNav(mq.matches);
    if (!mq.matches) setOpenMenuKey(null);
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}, []);

useEffect(() => {
  if (!touchNav || openMenuKey === null) return;
  const onDocClick = (e) => {
    if (!scrollRef.current?.contains(e.target)) {
      setOpenMenuKey(null);
    }
  };
  document.addEventListener('click', onDocClick);
  return () => document.removeEventListener('click', onDocClick);
}, [touchNav, openMenuKey]);

const updateArrows = useCallback(() => {
  const el = scrollRef.current;
  if (!el) return;
  const { scrollLeft, scrollWidth, clientWidth } = el;
  const epsilon = 2;
  const overflows = scrollWidth > clientWidth + epsilon;
  if (!overflows) {
    setArrows({ left: false, right: false });
    return;
  }
  setArrows({
    left: scrollLeft > epsilon,
    right: scrollLeft < scrollWidth - clientWidth - epsilon,
  });
}, []);

const SUBMENU_VIEWPORT_PADDING = 8;

const applySubmenuAlignment = useCallback(() => {
  const root = titlesRef.current;
  const navEl = scrollRef.current;
  if (!root) return;

  const boundary = navEl
    ? navEl.getBoundingClientRect().right - SUBMENU_VIEWPORT_PADDING
    : window.innerWidth - SUBMENU_VIEWPORT_PADDING;

  root.querySelectorAll(':scope > ul > li').forEach((li) => {
    const submenu = li.querySelector(':scope > ul');
    if (!submenu) {
      li.classList.remove('nav-submenu-align-right');
      return;
    }

    const isOpen = touchNav
      ? li.classList.contains('nav-item-open')
      : li.matches(':hover');

    if (!isOpen) {
      li.classList.remove('nav-submenu-align-right');
      return;
    }

    const liRect = li.getBoundingClientRect();
    const submenuWidth = submenu.offsetWidth || 150;
    li.classList.toggle('nav-submenu-align-right', liRect.left + submenuWidth > boundary);
  });
}, [touchNav]);

useLayoutEffect(() => {
  const el = scrollRef.current;
  if (!el) return;

  const onLayoutChange = () => {
    updateArrows();
    applySubmenuAlignment();
  };

  onLayoutChange();

  const ro = new ResizeObserver(onLayoutChange);
  ro.observe(el);
  const titles = titlesRef.current;
  if (titles) ro.observe(titles);

  el.addEventListener('scroll', onLayoutChange, { passive: true });
  window.addEventListener('resize', onLayoutChange);

  return () => {
    ro.disconnect();
    el.removeEventListener('scroll', onLayoutChange);
    window.removeEventListener('resize', onLayoutChange);
  };
}, [updateArrows, applySubmenuAlignment]);

const handleNavMouseOver = (e) => {
  if (touchNav) return;
  const li = e.target.closest('.nav-titles > ul > li');
  if (li?.querySelector(':scope > ul')) {
    requestAnimationFrame(applySubmenuAlignment);
  }
};

const handleNavMouseLeave = (e) => {
  if (touchNav) return;
  const next = e.relatedTarget;
  if (next instanceof Node && e.currentTarget.contains(next)) return;
  applySubmenuAlignment();
};

const handleClick = (e) => {
  const modulesArray = e.target.id.split('.')
  props.setModule(modulesArray)
  if (touchNav) setOpenMenuKey(null)
}

const handleMenuTitleClick = (key) => (e) => {
  if (!touchNav) return;
  e.stopPropagation();
  setOpenMenuKey((prev) => (prev === key ? null : key));
};

const menuItemClass = (key) => (touchNav && openMenuKey === key ? 'nav-item-open' : undefined);

useLayoutEffect(() => {
  applySubmenuAlignment();
}, [openMenuKey, applySubmenuAlignment]);

// Función para desplazamiento con flechas
const scroll = (direction) => {
  if (scrollRef.current) {
    const scrollAmount = 200;
    scrollRef.current.scrollLeft += direction === "left" ? -scrollAmount : scrollAmount;
    requestAnimationFrame(updateArrows);
  }
};
 
  return (
    <div className='nav-container'>
      {arrows.left && (
        <button type="button" className="arrow-nav arrow-nav-left" onClick={() => scroll("left")} aria-label="Desplazar menú a la izquierda">
          <ChevronLeft size={20} />
        </button>
      )}
      <nav className="nav-main notranslate" ref={scrollRef} translate="no">
        <div className="nav-titles" ref={titlesRef}>
          <ul onMouseOver={handleNavMouseOver} onMouseLeave={handleNavMouseLeave}>
          {auth("hot", "r") && (
              <li className={menuItemClass('hot')}>
                <p onClick={handleMenuTitleClick('hot')}>HOTEL</p>
                <ul>
                  <li>
                    <p id="hot" onClick={handleClick}>Reservas</p>
                  </li>
                  {auth("hot.och", "r") && (
                    <li>
                      <p id="hot.och" onClick={handleClick}>Check-in Online</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("agr", "r") && (
              <li className={menuItemClass('agr')}>
                <p onClick={handleMenuTitleClick('agr')}>AGRO</p>
                <ul>
                  {auth("agr.cam", "r") && (
                    <li>
                      <p id="agr.cam" onClick={handleClick}>Campos</p>
                    </li>
                  )}
                  {(auth("agr.cul", "r") || auth("agr.cpa", "r") || auth("agr.sie", "r")) && (
                    <li>
                      <p id="agr.cul" onClick={handleClick}>Cultivos</p>
                    </li>
                  )}
                  {(auth("agr.eve", "r") || auth("agr.evm", "r")) && (
                    <li>
                      <p id="agr.eve" onClick={handleClick}>Eventos</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("bod", "r") && (
              <li className={menuItemClass('bod')}>
                <p onClick={handleMenuTitleClick('bod')}>BODEGA</p>
                <ul>
                  {auth("bod.vas", "r") && (
                    <li>
                      <p id="bod.vas" onClick={handleClick}>Vasijas</p>
                    </li>
                  )}
                  {auth("bod.cos", "r") && (
                    <li>
                      <p id="bod.cos" onClick={handleClick}>Cosecha</p>
                    </li>
                  )}
                  {auth("bod.ela", "r") && (
                    <li>
                      <p id="bod.ela" onClick={handleClick}>Elaboración</p>
                    </li>
                  )}
                  {auth("bod.fra", "r") && (
                    <li>
                      <p id="bod.fra" onClick={handleClick}>Fraccionamiento</p>
                    </li>
                  )}
                  {auth("bod.ord", "r") && (
                    <li>
                      <p id="bod.ord" onClick={handleClick}>Órdenes de trabajo</p>
                    </li>
                  )}
                  {auth("bod.mae", "r") && (
                    <li>
                      <p id="bod.mae" onClick={handleClick}>Maestros</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("sub", "r") && (
              <li className={menuItemClass('sub')}>
                <p onClick={handleMenuTitleClick('sub')}>SUSCRIPCIONES</p>
                <ul>
                  {auth("sub.pla", "r") && (
                    <li>
                      <p id="sub.pla" onClick={handleClick}>Planes</p>
                    </li>
                  )}
                  {auth("sub.scr", "r") && (
                    <li>
                      <p id="sub.scr" onClick={handleClick}>Suscriptores</p>
                    </li>
                  )}
                  {auth("sub.con", "r") && (
                    <li>
                      <p id="sub.con" onClick={handleClick}>Contratos</p>
                    </li>
                  )}
                  {auth("sub.cuo", "r") && (
                    <li>
                      <p id="sub.cuo" onClick={handleClick}>Cuotas y mora</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("adm", "r") && (
              <li className={menuItemClass('adm')}>
                <p onClick={handleMenuTitleClick('adm')}>ADMINISTRACION</p>
                <ul>
                  {auth("adm.sal", "r") &&
                  <li>
                    <p id = "adm.sal" onClick={handleClick}>Ventas</p>
                  </li>
                  }
                  {auth("adm.pur", "r") &&
                  <li>
                    <p id = "adm.pur" onClick={handleClick}>Compras</p>
                  </li>
                  }
                  {auth("fin.col", "r") &&
                  <li>
                    <p id = "adm.col" onClick={handleClick}>Cobros</p>
                  </li>
                  }
                  {auth ("fin.pay", "r") &&
                  <li>
                    <p id="adm.pay" onClick={handleClick}>Pagos</p>
                  </li>
                  }
                  {auth("fin.exp", "r") &&
                  <li>
                    <p id="adm.exp" onClick={handleClick}>Ingresos y Gastos</p>
                  </li>
                  }
                  {auth("adm.pro", "r") &&
                  <li>
                    <p id="adm.pro" onClick={handleClick}>Productos</p>
                  </li>
                  }
                  {auth("adm.aud", "r") &&
                  <li>
                    <p id="adm.aud" onClick={handleClick}>Auditorías</p>
                  </li>
                  }
                  {auth("adm.com", "r") &&
                  <li>
                    <p id="adm.com" onClick={handleClick}>Comercial</p>
                  </li>
                  }
                  {auth("adm.ter", "r") &&
                  <li>
                    <p id="adm.sales-terminal" onClick={handleClick}>Terminal de Ventas</p>
                  </li>
                  }
                  </ul>
              </li>
            )}
            {auth("fin", "r") && (
              <li className={menuItemClass('fin')}>
                <p onClick={handleMenuTitleClick('fin')}>FINANZAS</p>

                <ul>
                  {auth("fin.cas", "r") &&
                  <li>
                    <p id="fin.cas" onClick={handleClick}>Caja</p>
                  </li>
                  }
                  { auth ("fin.ban", "r") &&
                  <li>
                    <p id="fin.ban" onClick={handleClick}>Bancos</p>
                  </li>

                  }
                  { auth ("fin.crc", "r") &&
                  <li>
                    <p id="fin.crc" onClick={handleClick}>Tarjetas</p>
                  </li>
                  }
                  { auth ("fin.occ", "r") &&
                  <li>
                    <p id="fin.occ" onClick={handleClick}>Tarjetas Propias</p>
                  </li>
                  }
                  { auth ("fin.chr", "r") &&
                  <li>
                    <p id="fin.chr" onClick={handleClick}>Cheques Recibidos</p>
                  </li>
                  }
                  { auth ("fin.che", "r") &&
                  <li>
                    <p id="fin.che" onClick={handleClick}>Cheques Emitidos</p>
                  </li>
                  }
                  { auth ("fin.qrc", "r") &&
                  <li>
                    <p id="fin.qrc" onClick={handleClick}>Cobros QR</p>
                  </li>
                  }
                  { auth ("fin.inv", "r") &&
                  <li>
                    <p id="fin.inv" onClick={handleClick}>Inversiones</p>
                  </li>
                  }
                  { auth ("fin", "r") &&
                  <li>
                    <p id="fin.crr" onClick={handleClick}>Riesgo Crediticio</p>
                  </li>
                  }
                </ul>
              </li>
            )}
            {auth("lab", "r") && (
              <li className={menuItemClass('lab')}>
                <p onClick={handleMenuTitleClick('lab')}>LABORAL</p>
                <ul>
                  {auth("lab.emp", "r") && (
                    <li>
                      <p id="lab.emp" onClick={handleClick}>
                        Empleados
                      </p>
                    </li>
                  )}
                  {auth("lab.sal", "r") && (
                    <li>
                      <p id="lab.sal" onClick={handleClick}>
                        Salarios
                      </p>
                    </li>
                  )}
                  {auth("lab.cat", "r") && (
                    <li>
                      <p id="lab.cat" onClick={handleClick}>
                        Categorías
                      </p>
                    </li>
                  )}
                  {auth("lab.con", "r") && (
                    <li>
                      <p id="lab.con" onClick={handleClick}>
                        Conceptos
                      </p>
                    </li>
                  )}
                  {auth("lab.rec", "r") && (
                    <li>
                      <p id="lab.rec" onClick={handleClick}>
                        Liquidación
                      </p>
                    </li>
                  )}
                  {auth("lab.lsd", "r") && (
                    <li>
                      <p id="lab.lsd" onClick={handleClick}>
                        Libro de Sueldos
                      </p>
                    </li>
                  )}
                  {auth("lab.cso", "r") && (
                    <li>
                      <p id="lab.cso" onClick={handleClick}>
                        Cargas Sociales
                      </p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("tax", "r") && (
              <li className={menuItemClass('tax')}>
                <p onClick={handleMenuTitleClick('tax')}>IMPUESTOS</p>
                <ul>
                  {auth("tax.set", "r") &&
                  <li>
                    <p id="tax.set" onClick={handleClick}>Configuración</p>
                  </li>
                  }
                  {auth("tax.per", "r") &&
                  <li>
                    <p id="tax.per" onClick={handleClick}>Agente de Percepción</p>
                  </li>
                  }
                  {auth("tax.suf", "r") &&
                  <li>
                    <p id="tax.suf" onClick={handleClick}>Perc. y Ret. Sufridas</p>
                  </li>
                  }
                  {auth("tax.iva", "r") &&
                  <li>
                    <p id="tax.iva" onClick={handleClick}>IVA</p>
                  </li>
                  }
                  {auth("tax.obl", "r") &&
                  <li>
                    <p id="tax.obl" onClick={handleClick}>Obligaciones impositivas</p>
                  </li>
                  }
                </ul>
              </li>
            )}
            {auth("acc", "r") && (
              <li className={menuItemClass('acc')}>
                <p onClick={handleMenuTitleClick('acc')}>CONTABILIDAD</p>
                <ul>
                  {auth("acc.cfg", "r") && (
                    <li>
                      <p id="acc.cfg" onClick={handleClick}>Configuración</p>
                    </li>
                  )}
                  {auth("acc.ast", "r") && (
                    <li>
                      <p id="acc.ast" onClick={handleClick}>Bienes de uso</p>
                    </li>
                  )}
                  {(auth("acc.plc", "r") || auth("acc.gen", "r") || auth("acc.jel", "r") || auth("acc.inf", "r")) && (
                    <li>
                      <p id="acc.jou" onClick={handleClick}>Asientos</p>
                    </li>
                  )}
                  {auth("acc.lib", "r") && (
                    <li>
                      <p id="acc.lib" onClick={handleClick}>Libros</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("met", "r") && (
              <li className={menuItemClass('met')}>
                <p onClick={handleMenuTitleClick('met')}>MÉTRICAS</p>
                <ul>
                  {auth("met.sal", "r") && (
                    <li>
                      <p id="met.sal" onClick={handleClick}>Ventas</p>
                    </li>
                  )}
                  {auth("met.sal", "r") && (
                    <li>
                      <p id="met.art" onClick={handleClick}>Productos</p>
                    </li>
                  )}
                  {auth("met.ter", "r") && (
                    <li>
                      <p id="met.ter" onClick={handleClick}>Terminales de Ventas</p>
                    </li>
                  )}
                  {auth("met.pur", "r") && (
                    <li>
                      <p id="met.pur" onClick={handleClick}>Compras</p>
                    </li>
                  )}
                  {auth("met.fin", "r") && (
                    <li>
                      <p id="met.fin" onClick={handleClick}>Finanzas</p>
                    </li>
                  )}
                  {auth("met.sub", "r") && (
                    <li>
                      <p id="met.sub" onClick={handleClick}>Suscripciones</p>
                    </li>
                  )}
                  {auth("met.pro", "r") && (
                    <li>
                      <p id="met.pro" onClick={handleClick}>Producción</p>
                    </li>
                  )}
                  {auth("met.boa", "r") && (
                    <li>
                      <p id="met.boa" onClick={handleClick}>Tablero de Control</p>
                    </li>
                  )}
                  {auth("hot.met", "r") && (
                    <li>
                      <p id="met.hot" onClick={handleClick}>Hotel</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("cos", "r") && (
              <li className={menuItemClass('cos')}>
                <p onClick={handleMenuTitleClick('cos')}>COSTEO</p>
                <ul>
                  {auth("cos.fic", "r") && (
                    <li>
                      <p id="cos.fic" onClick={handleClick}>Ficha de costeo</p>
                    </li>
                  )}
                  {auth("cos.val", "r") && (
                    <li>
                      <p id="cos.val" onClick={handleClick}>Valuación de stock</p>
                    </li>
                  )}
                  {auth("cos.ind", "r") && (
                    <li>
                      <p id="cos.ind" onClick={handleClick}>Costos indirectos</p>
                    </li>
                  )}
                  {auth("cos.std", "r") && (
                    <li>
                      <p id="cos.std" onClick={handleClick}>Costo estándar</p>
                    </li>
                  )}
                  {auth("cos.cfg", "r") && (
                    <li>
                      <p id="cos.cfg" onClick={handleClick}>Configuración</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("mel", "r") && (
              <li className={menuItemClass('mel')}>
                <p onClick={handleMenuTitleClick('mel')}>MERCADOLIBRE</p>
                <ul>
                  {auth("mel.con", "c") && (
                    <li>
                      <p id="mel.con" onClick={handleClick}>Conectar</p>
                    </li>
                  )}
                  {auth("mel.pub", "r") && (
                    <li>
                      <p id="mel.pub" onClick={handleClick}>Publicaciones</p>
                    </li>
                  )}
                  {auth("mel.ord", "r") && (
                    <li>
                      <p id="mel.ord" onClick={handleClick}>Pedidos</p>
                    </li>
                  )}
                  {auth("mel.que", "r") && (
                    <li>
                      <p id="mel.que" onClick={handleClick}>Preguntas</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {auth("sup", "r") && (
              <li className={menuItemClass('sup')}>
                <p onClick={handleMenuTitleClick('sup')}>SOPORTE</p>
                <ul>
                  {auth("sup.sal", "r") && (
                    <li>
                      <p id="sup.sal" onClick={handleClick}>Ventas</p>
                    </li>
                  )}
                  {auth("sup.pwd", "r") && (
                    <li>
                      <p id="sup.pwd" onClick={handleClick}>Reset contraseñas</p>
                    </li>
                  )}
                </ul>
              </li>
            )}
          </ul>
        </div>
        <div className='nav-titles'></div>
      </nav>
      {arrows.right && (
        <button type="button" className="arrow-nav arrow-nav-right" onClick={() => scroll("right")} aria-label="Desplazar menú a la derecha">
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  )
}

export default Nav
